import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { InputField } from '../../../../../../components/atoms/input-field';
import DestinationCard from '../../../atoms/destination-card';

import { useCreateDestinationGroupValidationSchema } from './schema/create-destination-group-validation-schema';

import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { Dialog, DialogActions } from '@/components/molecules/dialog';
import { CompactPagination } from '@/components/organisms/compact-pagination';
import { useCreateDestinationGroup } from '@/features/destination-groups/hooks/mutation/use-create-destination-group';
import { useDestinations } from '@/features/destinations/hooks/query/use-destinations';
import { usePagination } from '@/hooks/use-pagination';

interface FormValues {
  name: string;
  destinationsIds: number[];
}

const defaultValues: FormValues = {
  name: '',
  destinationsIds: [],
};

export const CreateDestinationGroupDialog = ({ onCancel, open }: DialogActions) => {
  const { t } = useTranslation(['destinationGroups', 'common', 'validation']);

  const {
    page,
    changePage,
    perPage,
    changePerPage,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
    setTotalItems,
    totalPages,
  } = usePagination();

  const { data, isLoading, isFetching, error } = useDestinations({ page, perPage });

  const { mutate, isPending } = useCreateDestinationGroup(onCancel);

  const validationSchema = useCreateDestinationGroupValidationSchema();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const onSubmit = (data: FormValues) => {
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      setTotalItems(data.total);
    }
  }, [data]);

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [open]);

  return (
    <Dialog
      onCancel={onCancel}
      open={open}
      title={t('destinationGroups:create')}
      variant={'info'}
      confirmButtonText={t('common:button.confirm')}
      isConfirmButtonDisabled={!isValid || !isDirty}
      isConfirmButtonSubmitting={isPending}
      onConfirm={handleSubmit(onSubmit)}
      pagination={
        <CompactPagination
          changePage={changePage}
          page={page}
          isFetching={isFetching}
          perPage={perPage}
          handlePerPageChange={changePerPage}
          disabled={isLoading}
          prevDisabled={prevDisabled}
          nextDisabled={nextDisabled}
          totalPages={totalPages}
          nextPage={nextPage}
          prevPage={prevPage}
        />
      }>
      <Controller
        name={'name'}
        control={control}
        render={({
          fieldState: { invalid, error },
          field: { name, onChange, ref, onBlur, value },
        }) => (
          <InputField
            fullWidth
            label={t('destinationGroups:dialog.name')}
            name={name}
            ref={ref}
            onBlur={onBlur}
            onChange={onChange}
            required
            value={value}
            helperText={error?.message}
            error={invalid}
          />
        )}
      />

      <Typography color="inherit" sx={{ my: 1 }} variant="body2">
        {t('destinationGroups:dialog.discount')}
      </Typography>
      <Controller
        control={control}
        name="destinationsIds"
        render={({ field: { value } }) => {
          const handleToggleDestinationCard = (destinationId: number) => {
            let valueCopy = [...value];

            if (valueCopy.some(selectedDestinationId => selectedDestinationId === destinationId)) {
              valueCopy = valueCopy.filter(
                selectedDestinationId => selectedDestinationId !== destinationId,
              );
            } else {
              valueCopy.push(destinationId);
            }

            setValue('destinationsIds', valueCopy, {
              shouldValidate: true,
              shouldDirty: true,
            });
          };

          return (
            <Box sx={{ my: 2 }}>
              {data?.resources?.map(({ name, id }) => {
                return (
                  <DestinationCard
                    key={id}
                    destinationId={id}
                    isSelected={value.some(selectedDestinationId => selectedDestinationId === id)}
                    destinationName={name}
                    toggleSelection={(_, id) => {
                      handleToggleDestinationCard(id);
                    }}
                  />
                );
              })}
              <DataStatusIndicator
                dataLength={data?.resources?.length ?? 0}
                isFetching={isLoading}
                error={error?.response?.data?.message}
              />
            </Box>
          );
        }}
      />
    </Dialog>
  );
};
